import {
  MSG_MATCH_FOUND,
  MSG_GAME_STATE,
  MSG_COUNTDOWN,
  MSG_GAME_OVER,
  MSG_LOBBY_STATUS,
  MSG_READY_STATUS,
  MatchInfo,
  GameStateMsg,
  ReadyStatus,
  decodeMatchFound,
  decodeGameState,
  decodeCountdown,
  decodeGameOver,
  decodeLobbyStatus,
  decodeReadyStatus,
  encodeReady,
  encodeInputState,
} from './protocol';

export type NetworkState = 'disconnected' | 'connecting' | 'connected' | 'matched' | 'countdown' | 'playing' | 'finished';

export interface NetworkCallbacks {
  onStateChange: (state: NetworkState) => void;
  onMatchFound: (info: MatchInfo) => void;
  onGameState: (state: GameStateMsg) => void;
  onCountdown: (ticksRemaining: number) => void;
  onGameOver: (winnerId: number) => void;
  onLobbyStatus?: (playerCount: number) => void;
  onReadyStatus?: (status: ReadyStatus) => void;
}

export class NetworkClient {
  private ws: WebSocket | null = null;
  private state: NetworkState = 'disconnected';
  private callbacks: NetworkCallbacks;
  private playerId: number = -1;

  constructor(callbacks: NetworkCallbacks) {
    this.callbacks = callbacks;
  }

  connect(url: string): void {
    if (this.ws) {
      this.ws.close();
    }

    this.setState('connecting');
    this.ws = new WebSocket(url);
    this.ws.binaryType = 'arraybuffer';

    this.ws.onopen = () => {
      this.setState('connected');
    };

    this.ws.onclose = () => {
      this.setState('disconnected');
    };

    this.ws.onerror = () => {
      this.setState('disconnected');
    };

    this.ws.onmessage = (event) => {
      this.handleMessage(event.data as ArrayBuffer);
    };
  }

  disconnect(): void {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.setState('disconnected');
  }

  sendReady(): void {
    this.send(encodeReady());
  }

  sendInput(
    left: boolean,
    right: boolean,
    rotateCW: boolean,
    rotateCCW: boolean,
    softDrop: boolean,
    hardDrop: boolean,
    hold: boolean,
    powerupSlot: number = 0
  ): void {
    this.send(encodeInputState(left, right, rotateCW, rotateCCW, softDrop, hardDrop, hold, powerupSlot));
  }

  getPlayerId(): number {
    return this.playerId;
  }

  getState(): NetworkState {
    return this.state;
  }

  private send(data: ArrayBuffer): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(data);
    }
  }

  private setState(state: NetworkState): void {
    this.state = state;
    this.callbacks.onStateChange(state);
  }

  private handleMessage(data: ArrayBuffer): void {
    if (data.byteLength < 1) return;

    const view = new DataView(data);
    const msgType = view.getUint8(0);

    switch (msgType) {
      case MSG_MATCH_FOUND: {
        const info = decodeMatchFound(data);
        this.playerId = info.playerId;
        this.setState('matched');
        this.callbacks.onMatchFound(info);
        break;
      }

      case MSG_GAME_STATE: {
        const state = decodeGameState(data);
        if (this.state !== 'playing') {
          this.setState('playing');
        }
        this.callbacks.onGameState(state);
        break;
      }

      case MSG_COUNTDOWN: {
        const ticks = decodeCountdown(data);
        if (this.state !== 'countdown') {
          this.setState('countdown');
        }
        this.callbacks.onCountdown(ticks);
        break;
      }

      case MSG_GAME_OVER: {
        const winnerId = decodeGameOver(data);
        this.setState('finished');
        this.callbacks.onGameOver(winnerId);
        break;
      }

      case MSG_LOBBY_STATUS: {
        this.callbacks.onLobbyStatus?.(decodeLobbyStatus(data));
        break;
      }

      case MSG_READY_STATUS: {
        this.callbacks.onReadyStatus?.(decodeReadyStatus(data));
        break;
      }
    }
  }
}
